import { X } from "lucide-react";
import {
  BOOKING_STATUS_STYLES,
  formatNairaDecimal,
} from "../../data/adminUsers";

// One row in a "Booking Details" section: label on the left, value on the right.
function DetailRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="max-w-[60%] whitespace-pre-wrap text-right text-sm font-semibold text-gray-900">
        {value}
      </span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="mb-1 mt-5 text-sm font-bold text-gray-900 first:mt-0">
      {children}
    </h3>
  );
}

function PaymentRow({ label, value, isTotal }) {
  return (
    <div
      className={`flex items-center justify-between py-2 ${
        isTotal ? "mt-1 border-t border-gray-100 pt-3" : ""
      }`}
    >
      <span
        className={`text-sm ${
          isTotal ? "font-bold text-gray-900" : "text-gray-500"
        }`}
      >
        {label}
      </span>
      <span
        className={`text-sm font-semibold ${
          isTotal ? "text-[#570033]" : "text-gray-900"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

// Renders the body for each of the three booking-detail variants.
function BookingBody({ details }) {
  if (!details) return null;

  if (details.type === "Ride") {
    const { driver, ride, payment } = details;
    return (
      <>
        <SectionTitle>Drivers Details</SectionTitle>
        <DetailRow label="Driver Name" value={driver.name} />
        <DetailRow label="Car Type" value={driver.carType} />
        <DetailRow label="Car Color" value={driver.carColor} />
        <DetailRow label="Number Plate" value={driver.numberPlate} />

        <SectionTitle>Ride Details</SectionTitle>
        <DetailRow label="Pick Up" value={ride.pickUp} />
        <DetailRow label="Drop Off" value={ride.dropOff} />
        <DetailRow label="Distance" value={ride.distance} />
        <DetailRow label="Duration" value={ride.duration} />

        <SectionTitle>Payment Summary</SectionTitle>
        <PaymentRow label="Base Fare" value={formatNairaDecimal(payment.baseFare)} />
        <PaymentRow
          label="Extra Charges (Traffic)"
          value={formatNairaDecimal(payment.extraCharges)}
        />
        <PaymentRow
          label="Platform Fee"
          value={formatNairaDecimal(payment.platformFee)}
        />
        <PaymentRow label="Total" value={formatNairaDecimal(payment.total)} isTotal />
      </>
    );
  }

  if (details.type === "Pulo Assistant") {
    const { assistant, trip, logistics, payment } = details;
    return (
      <>
        <SectionTitle>Pulo Assistant Details</SectionTitle>
        <DetailRow label="Pulo Assistant Name" value={assistant.name} />
        <DetailRow label="Car Type" value={assistant.carType} />
        <DetailRow label="Car Color" value={assistant.carColor} />
        <DetailRow label="Number Plate" value={assistant.numberPlate} />

        <SectionTitle>Trip Details</SectionTitle>
        <DetailRow label="Participants" value={trip.participants} />
        <DetailRow label="Location" value={trip.location} />
        <DetailRow label="Check in date" value={trip.checkInDate} />
        <DetailRow label="Check out date" value={trip.checkOutDate} />
        <DetailRow label="Itinerary" value={trip.itinerary || "—"} />

        <SectionTitle>Logistics</SectionTitle>
        <DetailRow label="Security Officers" value={logistics.securityOfficers} />
        <DetailRow label="Convoy Vehicles" value={logistics.convoyVehicles} />

        <SectionTitle>Payment Summary</SectionTitle>
        <PaymentRow
          label="Base Trip Cost"
          value={formatNairaDecimal(payment.baseTripCost)}
        />
        <PaymentRow
          label="Vehicle Cost"
          value={formatNairaDecimal(payment.vehicleCost)}
        />
        <PaymentRow
          label="Security Cost"
          value={formatNairaDecimal(payment.securityCost)}
        />
        <PaymentRow label="Total" value={formatNairaDecimal(payment.total)} isTotal />
      </>
    );
  }

  if (details.type === "Accommodation") {
    const { accommodation, payment } = details;
    return (
      <>
        <SectionTitle>Accommodation Details</SectionTitle>
        <DetailRow label="Property Name" value={accommodation.propertyName} />
        <DetailRow label="Address" value={accommodation.address} />
        <DetailRow label="Check-in date" value={accommodation.checkInDate} />
        <DetailRow label="Check-Out date" value={accommodation.checkOutDate} />
        <DetailRow label="Number of Guests" value={accommodation.guests} />

        <SectionTitle>Payment Summary</SectionTitle>
        <PaymentRow label="Room Cost" value={formatNairaDecimal(payment.roomCost)} />
        <PaymentRow
          label="Extra Charges"
          value={formatNairaDecimal(payment.extraCharges)}
        />
        <PaymentRow
          label="Service Fee"
          value={formatNairaDecimal(payment.serviceFee)}
        />
        <PaymentRow label="Total" value={formatNairaDecimal(payment.total)} isTotal />
      </>
    );
  }

  return null;
}

/* Contact actions are retained for the future email workflow.
const secondaryActionLabel = (type) => {
  if (type === "Ride") return "Contact Driver";
  if (type === "Accommodation") return "Contact Host";
  return "Contact Pulo Assistant";
};
*/

// Statuses where the traveller has paid and the assistant's money is held or
// only part-released — the same set the API will act on. Anything else has no
// payout to force: an unaccepted booking belongs to the refund path, and a
// completed one has already paid out.
const RELEASABLE_STATUSES = ["accepted", "assistant_arrived", "trip_started"];

function BookingDetailsModal({ booking, onClose, onReleasePayout, releasing }) {
  if (!booking) return null;

  const details = booking.details;
  const status = booking.status;
  const canReleasePayout =
    typeof onReleasePayout === "function" &&
    RELEASABLE_STATUSES.includes(booking.rawStatus);

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-[540px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <h2 className="text-lg font-bold text-gray-900">Booking Details</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 transition hover:bg-gray-50 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 pt-4">
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${
              BOOKING_STATUS_STYLES[status] || "bg-gray-100 text-gray-700"
            }`}
          >
            {status}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-4 pt-1">
          <BookingBody details={details} />
        </div>

        <div className="space-y-3 border-t border-gray-100 px-6 py-5">
          {/* Contact actions intentionally hidden. Admin contact should use email.
          <button
            type="button"
            onClick={onContactUser}
            className="w-full rounded-lg bg-[#570033] py-3 text-sm font-semibold text-white transition hover:bg-[#61013B]"
          >
            Contact User
          </button>
          <button
            type="button"
            onClick={onContactParty}
            className="w-full rounded-lg border border-[#570033] py-3 text-sm font-semibold text-[#570033] transition hover:bg-[#570033]/5"
          >
            {secondaryActionLabel(details?.type)}
          </button>
          */}
          {canReleasePayout ? (
            <div className="space-y-2">
              <button
                type="button"
                onClick={onReleasePayout}
                disabled={releasing}
                className="w-full rounded-lg bg-[#570033] py-3 text-sm font-semibold text-white transition hover:bg-[#61013B] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {releasing ? "Releasing..." : "Release held payout"}
              </button>
              <p className="text-xs text-gray-500">
                Use this when the trip ran but one side never confirmed it.
                Closes the booking and pays the assistant whatever it still
                owes.
              </p>
            </div>
          ) : null}

          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-[#570033] py-3 text-sm font-semibold text-[#570033] transition hover:bg-[#570033]/5"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default BookingDetailsModal;
